import React, { useState } from 'react';

// Define types for our component
interface JsonToIcalProps {}

// Calendar file format options
type CalendarFormat = 'ics' | 'ical' | 'ifb';

// Valid event status types
type EventStatus = 'CONFIRMED' | 'TENTATIVE' | 'CANCELLED';

// Valid event transparency types
type EventTransparency = 'OPAQUE' | 'TRANSPARENT';

// Calendar event model
interface CalendarEvent {
  summary: string;
  description?: string;
  location?: string;
  url?: string;
  start: string; // ISO format date or datetime
  end?: string; // ISO format date or datetime
  allDay?: boolean;
  rrule?: string; // Recurrence rule
  status?: EventStatus;
  transparency?: EventTransparency;
  categories?: string[];
  organizer?: {
    name?: string;
    email: string;
  };
  attendees?: {
    name?: string;
    email: string;
    rsvp?: boolean;
    role?: string;
  }[];
}

const JsonToIcal: React.FC<JsonToIcalProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the iCal output
  const [icalOutput, setIcalOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('calendar');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for file format
  const [fileFormat, setFileFormat] = useState<CalendarFormat>('ics');
  // State to track if the conversion happened
  const [isIcalGenerated, setIsIcalGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for the calendar name
  const [calendarName, setCalendarName] = useState<string>('My Calendar');
  // State for the calendar description
  const [calendarDescription, setCalendarDescription] = useState<string>('');
  // State for the product ID
  const [productId, setProductId] = useState<string>('-//JsonToIcal//EN');

  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsIcalGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    const exampleData: CalendarEvent[] = [
      {
        summary: 'Team Meeting',
        description: 'Weekly team meeting to discuss project progress',
        location: 'Conference Room 3A',
        start: '2025-06-15T09:00:00',
        end: '2025-06-15T10:30:00',
        status: 'CONFIRMED',
        transparency: 'OPAQUE',
        categories: ['Work', 'Meeting'],
        organizer: {
          name: 'Jane Smith',
          email: 'jane.smith@example.com'
        },
        attendees: [
          {
            name: 'John Doe',
            email: 'john.doe@example.com',
            rsvp: true,
            role: 'REQ-PARTICIPANT'
          },
          {
            name: 'Alice Johnson',
            email: 'alice.johnson@example.com',
            rsvp: true,
            role: 'OPT-PARTICIPANT'
          }
        ]
      },
      {
        summary: 'Doctor Appointment',
        description: 'Annual checkup',
        location: 'Medical Center, Room 205',
        start: '2025-06-18T14:00:00',
        end: '2025-06-18T15:00:00',
        status: 'CONFIRMED',
        categories: ['Personal', 'Health']
      },
      {
        summary: 'Full Day Conference',
        description: 'Annual industry conference',
        location: 'Convention Center',
        start: '2025-06-20',
        allDay: true,
        status: 'CONFIRMED',
        categories: ['Work', 'Conference']
      },
      {
        summary: 'Weekly Team Standup',
        description: 'Weekly standup meeting',
        location: 'Virtual Meeting Room',
        start: '2025-06-16T09:00:00',
        end: '2025-06-16T09:30:00',
        rrule: 'FREQ=WEEKLY;BYDAY=MO;COUNT=10',
        status: 'CONFIRMED',
        categories: ['Work', 'Recurring']
      }
    ];
    
    setJsonInput(JSON.stringify(exampleData, null, 2));
    setError('');
    setIsIcalGenerated(false);
    setIcalOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setIcalOutput('');
    setError('');
    setFileName('calendar');
    setCalendarName('My Calendar');
    setCalendarDescription('');
    setProductId('-//JsonToIcal//EN');
    setFileFormat('ics');
    setIsIcalGenerated(false);
  };

  // Generate a random UID for an event
  const generateUID = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Format a date string for iCalendar (removes dashes, colons, etc.)
  const formatICalDate = (dateStr: string, allDay: boolean = false): string => {
    if (!dateStr) return '';
    
    const date = new Date(dateStr);
    
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date format: ${dateStr}`);
    }
    
    // For all-day events, just use the date component in YYYYMMDD format
    if (allDay) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}${month}${day}`;
    }
    
    // For time-based events, use full timestamp in YYYYMMDDTHHMMSSZ format
    const isoString = date.toISOString();
    return isoString.replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };

  // Escape special characters in iCalendar text values
  const escapeText = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  };

  // Fold long lines according to the iCalendar spec (lines should be <= 75 chars)
  const foldLine = (line: string): string => {
    if (line.length <= 75) {
      return line;
    }
    
    let result = '';
    for (let i = 0; i < line.length; i += 75) {
      if (i === 0) {
        result += line.slice(i, Math.min(i + 75, line.length)) + '\r\n';
      } else {
        result += ' ' + line.slice(i, Math.min(i + 74, line.length)) + (i + 74 < line.length ? '\r\n' : '');
      }
    }
    
    return result;
  };

  // Convert a CalendarEvent to iCalendar format
  const eventToIcal = (event: CalendarEvent): string => {
    let ical = 'BEGIN:VEVENT\r\n';
    
    // Required properties
    ical += `UID:${generateUID()}@jsontoical.example.com\r\n`;
    ical += `DTSTAMP:${formatICalDate(new Date().toISOString())}\r\n`;
    
    // Add summary (required)
    ical += `SUMMARY:${escapeText(event.summary)}\r\n`;
    
    // Add start date/time (required)
    if (event.allDay) {
      ical += `DTSTART;VALUE=DATE:${formatICalDate(event.start, true)}\r\n`;
      
      // For all-day events, if end date is not provided, use start date
      const endDate = event.end || event.start;
      
      // For all-day events, the end date is exclusive, so we need to add one day
      const endDateObj = new Date(endDate);
      endDateObj.setDate(endDateObj.getDate() + 1);
      const adjustedEndDate = endDateObj.toISOString();
      
      ical += `DTEND;VALUE=DATE:${formatICalDate(adjustedEndDate, true)}\r\n`;
    } else {
      ical += `DTSTART:${formatICalDate(event.start)}\r\n`;
      
      // Add end date/time if provided
      if (event.end) {
        ical += `DTEND:${formatICalDate(event.end)}\r\n`;
      }
    }
    
    // Add description if provided
    if (event.description) {
      ical += foldLine(`DESCRIPTION:${escapeText(event.description)}`);
      ical += '\r\n';
    }
    
    // Add location if provided
    if (event.location) {
      ical += foldLine(`LOCATION:${escapeText(event.location)}`);
      ical += '\r\n';
    }
    
    // Add URL if provided
    if (event.url) {
      ical += foldLine(`URL:${escapeText(event.url)}`);
      ical += '\r\n';
    }
    
    // Add recurrence rule if provided
    if (event.rrule) {
      ical += `RRULE:${event.rrule}\r\n`;
    }
    
    // Add status if provided
    if (event.status) {
      ical += `STATUS:${event.status}\r\n`;
    }
    
    // Add transparency if provided
    if (event.transparency) {
      ical += `TRANSP:${event.transparency}\r\n`;
    }
    
    // Add categories if provided
    if (event.categories && event.categories.length > 0) {
      ical += `CATEGORIES:${event.categories.map(escapeText).join(',')}\r\n`;
    }
    
    // Add organizer if provided
    if (event.organizer) {
      const organizer = event.organizer;
      let organizerStr = `ORGANIZER:MAILTO:${organizer.email}`;
      if (organizer.name) {
        organizerStr = `ORGANIZER;CN=${escapeText(organizer.name)}:MAILTO:${organizer.email}`;
      }
      ical += foldLine(organizerStr);
      ical += '\r\n';
    }
    
    // Add attendees if provided
    if (event.attendees && event.attendees.length > 0) {
      event.attendees.forEach(attendee => {
        let attendeeStr = `ATTENDEE:MAILTO:${attendee.email}`;
        
        const params = [];
        if (attendee.name) {
          params.push(`CN=${escapeText(attendee.name)}`);
        }
        if (attendee.role) {
          params.push(`ROLE=${attendee.role}`);
        }
        if (attendee.rsvp !== undefined) {
          params.push(`RSVP=${attendee.rsvp ? 'TRUE' : 'FALSE'}`);
        }
        
        if (params.length > 0) {
          attendeeStr = `ATTENDEE;${params.join(';')}:MAILTO:${attendee.email}`;
        }
        
        ical += foldLine(attendeeStr);
        ical += '\r\n';
      });
    }
    
    ical += 'END:VEVENT\r\n';
    return ical;
  };

  // Convert JSON to iCalendar format
  const convertJsonToIcal = () => {
    setError('');
    setIsLoading(true);
    setIsIcalGenerated(false);
    setIcalOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const events: CalendarEvent[] = JSON.parse(jsonInput);
      
      if (!Array.isArray(events)) {
        setError('JSON input must be an array of event objects');
        setIsLoading(false);
        return;
      }
      
      // Start building the iCalendar file
      let icalContent = 'BEGIN:VCALENDAR\r\n';
      icalContent += 'VERSION:2.0\r\n';
      icalContent += `PRODID:${productId}\r\n`;
      icalContent += 'CALSCALE:GREGORIAN\r\n';
      icalContent += 'METHOD:PUBLISH\r\n';
      
      // Add calendar name if provided
      if (calendarName) {
        icalContent += `X-WR-CALNAME:${escapeText(calendarName)}\r\n`;
      }
      
      // Add calendar description if provided
      if (calendarDescription) {
        icalContent += foldLine(`X-WR-CALDESC:${escapeText(calendarDescription)}`);
        icalContent += '\r\n';
      }
      
      // Process each event
      for (const event of events) {
        try {
          icalContent += eventToIcal(event);
        } catch (eventError) {
          setError(`Error processing event "${event.summary}": ${eventError instanceof Error ? eventError.message : String(eventError)}`);
          setIsLoading(false);
          return;
        }
      }
      
      // End the calendar
      icalContent += 'END:VCALENDAR';
      
      setIcalOutput(icalContent);
      setIsIcalGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating iCalendar: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy iCalendar content to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(icalOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download iCalendar file
  const downloadIcal = () => {
    const blob = new Blob([icalOutput], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'calendar'}.${fileFormat}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to iCalendar Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to standard iCalendar format (.ics files) for use with calendar applications. 
          Paste your JSON data below to generate a downloadable iCalendar file.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON event data here, e.g., [{"summary": "Team Meeting", "start": "2025-06-15T09:00:00", "end": "2025-06-15T10:30:00"}]'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                  File Name
                </label>
                <input
                  type="text"
                  id="fileName"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={fileName}
                  onChange={handleFileNameChange}
                  placeholder="Enter file name (without extension)"
                />
              </div>
              <div>
                <label htmlFor="fileFormat" className="block text-sm font-medium text-gray-700 mb-1">
                  File Format
                </label>
                <select
                  id="fileFormat"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={fileFormat}
                  onChange={(e) => setFileFormat(e.target.value as CalendarFormat)}
                >
                  <option value="ics">ICS</option>
                  <option value="ical">iCalendar</option>
                  <option value="ifb">IFB</option>
                </select>
              </div>
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="calendarName" className="block text-sm font-medium text-gray-700 mb-1">
                    Calendar Name
                  </label>
                  <input
                    type="text"
                    id="calendarName"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={calendarName}
                    onChange={(e) => setCalendarName(e.target.value)}
                    placeholder="Enter calendar name"
                  />
                </div>
                
                <div>
                  <label htmlFor="calendarDescription" className="block text-sm font-medium text-gray-700 mb-1">
                    Calendar Description
                  </label>
                  <input
                    type="text"
                    id="calendarDescription"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={calendarDescription}
                    onChange={(e) => setCalendarDescription(e.target.value)}
                    placeholder="Enter calendar description"
                  />
                </div>
                
                <div>
                  <label htmlFor="productId" className="block text-sm font-medium text-gray-700 mb-1">
                    Product ID
                  </label>
                  <input
                    type="text"
                    id="productId"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                    placeholder="Enter product ID"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Used to identify the product that created the iCalendar object
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToIcal}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to iCalendar'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              iCalendar Output
            </label>
            
            {isIcalGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy iCalendar'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadIcal}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download .{fileFormat}
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isIcalGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>
                    iCalendar output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isIcalGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="icalOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-xs focus:ring-blue-500 focus:border-blue-500"
                    value={icalOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isIcalGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">iCalendar Successfully Generated!</p>
              </div>
              <p className="text-sm">
                The generated iCalendar file is compatible with all major calendar applications including Google Calendar, 
                Apple Calendar, Microsoft Outlook, and more.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to iCalendar Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool transforms your JSON event data into standard iCalendar format (.ics files) that can be imported into 
              any calendar application. The converter follows the RFC 5545 specification for iCalendar format.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Accepted JSON Format</h3>
            <p className="text-gray-600">
              The input should be a JSON array of event objects. Each event object should have at least a "summary" and a "start" property.
              The "start" (and optional "end") should be in ISO format (e.g., "2025-06-15T09:00:00" or "2025-06-15" for all-day events).
            </p>
            <div className="mt-2 bg-gray-50 p-3 rounded-md font-mono text-xs">
              <pre>{`[
  {
    "summary": "Team Meeting",
    "description": "Weekly team meeting",
    "location": "Conference Room",
    "start": "2025-06-15T09:00:00",
    "end": "2025-06-15T10:30:00"
  },
  {
    "summary": "All Day Event",
    "start": "2025-06-20",
    "allDay": true
  }
]`}</pre>
            </div>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Supported Properties</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>summary</strong> - Event title (required)</li>
              <li><strong>start</strong> - Start date/time in ISO format (required)</li>
              <li><strong>end</strong> - End date/time in ISO format (optional)</li>
              <li><strong>allDay</strong> - Boolean indicating if it's an all-day event (optional)</li>
              <li><strong>description</strong> - Detailed description of the event (optional)</li>
              <li><strong>location</strong> - Event location (optional)</li>
              <li><strong>url</strong> - URL associated with the event (optional)</li>
              <li><strong>rrule</strong> - Recurrence rule for repeating events (optional)</li>
              <li><strong>status</strong> - Event status: CONFIRMED, TENTATIVE, or CANCELLED (optional)</li>
              <li><strong>transparency</strong> - Whether the event blocks time: OPAQUE or TRANSPARENT (optional)</li>
              <li><strong>categories</strong> - Array of category strings (optional)</li>
              <li><strong>organizer</strong> - Object with name and email of organizer (optional)</li>
              <li><strong>attendees</strong> - Array of attendee objects with name, email, etc. (optional)</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Always include a summary and start time for each event</li>
              <li>Use ISO format dates (YYYY-MM-DDTHH:MM:SS) for start and end times</li>
              <li>For all-day events, use date format (YYYY-MM-DD) and set allDay to true</li>
              <li>For recurring events, use the rrule property with a valid RRULE string (e.g., "FREQ=WEEKLY;BYDAY=MO")</li>
              <li>The generated file can be imported into any calendar application that supports iCalendar format</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToIcal;